import { cur } from '@/lib/currency';
import { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, DollarSign, ShoppingCart, Users, Clock, Trophy, BarChart3, RefreshCw, Download, TrendingDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { DateQuickFilter } from '@/components/DateQuickFilter';
import { BranchMultiSelect } from '@/components/BranchMultiSelect';
import { Switch } from '@/components/ui/switch';
import { Tooltip as UITooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { localToday } from '@/lib/dateRange';

const PIE_COLORS = ['#22c55e', '#3b82f6', '#8b5cf6', '#f59e0b'];

// vs-Previous-Period delta chip with a hover tooltip showing the exact prior
// figure. `invert` flips good/bad colour (Expenses). `money` formats prev as SR.
function Delta({ value, prev, label = 'value', money = false, invert = false }) {
  if (value === null || value === undefined) return null;
  const up = value >= 0;
  const good = invert ? !up : up;
  const Icon = up ? TrendingUp : TrendingDown;
  const prevStr = (prev === null || prev === undefined)
    ? '—'
    : (money ? `${cur()} ${Number(prev).toLocaleString()}` : Number(prev).toLocaleString());
  return (
    <TooltipProvider delayDuration={100}>
      <UITooltip>
        <TooltipTrigger asChild>
          <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold mt-0.5 cursor-help ${good ? 'text-emerald-600' : 'text-rose-600'}`} data-testid="kpi-delta">
            <Icon size={10} />{up ? '+' : ''}{value}% <span className="text-muted-foreground font-normal">vs prev</span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          Previous {label}: <strong>{prevStr}</strong>
        </TooltipContent>
      </UITooltip>
    </TooltipProvider>
  );
}

export default function POSAnalyticsPage() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [branches, setBranches] = useState([]);
  // Smart defaults: date = Today, branch = All Branches ([]).
  const [dateFilter, setDateFilter] = useState({ start: localToday(), end: localToday() });
  const [branchFilter, setBranchFilter] = useState([]);
  const [hideZero, setHideZero] = useState(false);
  const [exporting, setExporting] = useState(false);
  const intervalRef = useRef(null);
  // Keep the latest filters in a ref so the 12s interval always fetches the
  // currently-selected scope without re-subscribing the timer.
  const filtersRef = useRef({ dateFilter, branchFilter, hideZero });
  filtersRef.current = { dateFilter, branchFilter, hideZero };

  const fetchData = async () => {
    try {
      const { dateFilter: df, branchFilter: bf, hideZero: hz } = filtersRef.current;
      const params = {};
      if (df?.start) params.start_date = df.start;
      if (df?.end) params.end_date = df.end;
      if (bf && bf.length) params.branch_ids = bf.join(',');
      if (hz) params.hide_zero = true;
      const { data: d } = await api.get('/dashboard/live-analytics', { params });
      setData(d);
      setLastUpdate(new Date());
    } catch {}
  };

  useEffect(() => {
    api.get('/branches').then(({ data }) => setBranches((data || []).map(b => ({ id: b.id, name: b.name })))).catch(() => {});
  }, []);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const { dateFilter: df, branchFilter: bf, hideZero: hz } = filtersRef.current;
      const params = {};
      if (df?.start) params.start_date = df.start;
      if (df?.end) params.end_date = df.end;
      if (bf && bf.length) params.branch_ids = bf.join(',');
      if (hz) params.hide_zero = true;
      const res = await api.get('/dashboard/live-analytics/export-pdf', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `live-pos-analytics-${df?.start || 'today'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Snapshot exported');
    } catch {
      toast.error('Export failed');
    } finally { setExporting(false); }
  };

  // Human label for the selected range, used in dynamic empty-state text.
  const rangeLabel = (!dateFilter.start && !dateFilter.end)
    ? 'today'
    : (dateFilter.start === dateFilter.end ? dateFilter.start : `${dateFilter.start} → ${dateFilter.end}`);

  // Refetch immediately when filters change; (re)arm the 12s live refresh.
  useEffect(() => {
    fetchData();
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(fetchData, 12000);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter.start, dateFilter.end, branchFilter, hideZero]);

  const modeData = data ? Object.entries(data.payment_modes || {}).filter(([, v]) => v > 0).map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: Math.round(v) })) : [];

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="pos-analytics-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-4">

              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-200/50">

                <BarChart3 size={24} className="text-white" />

              </div>

              <div>

                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="analytics-title">Live POS Analytics</h1>

                <p className="text-sm text-muted-foreground mt-0.5">Live POS analytics and insights</p>

              </div>

            </div>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <span className="w-2 h-2 bg-success rounded-full animate-pulse"></span>
              Auto-refreshing every 12s {lastUpdate && `| Last: ${lastUpdate.toLocaleTimeString()}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportPdf} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-xs font-semibold hover:bg-muted transition-colors disabled:opacity-50" data-testid="pos-analytics-export-btn">
              <Download size={14} /> {exporting ? 'Exporting…' : 'Export PDF'}
            </button>
            <button onClick={fetchData} className="p-2 rounded-lg hover:bg-muted transition-colors" data-testid="refresh-btn">
              <RefreshCw size={16} className="text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Universal Filters — reused BranchMultiSelect + DateQuickFilter (Sales Summary) */}
        <div className="flex flex-wrap items-center gap-3" data-testid="pos-analytics-filters">
          <BranchMultiSelect
            value={branchFilter}
            onChange={setBranchFilter}
            branches={branches}
            testid="pos-analytics-branch-select"
          />
          <DateQuickFilter
            defaultActive="today"
            onFilterChange={(r) => setDateFilter(r ? { start: r.start, end: r.end } : { start: null, end: null })}
          />
          <label className="inline-flex items-center gap-2 ml-auto cursor-pointer select-none" data-testid="hide-zero-toggle">
            <Switch checked={hideZero} onCheckedChange={setHideZero} data-testid="hide-zero-switch" />
            <span className="text-xs font-medium text-muted-foreground">Hide SR 0 Sales</span>
          </label>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="kpi-cards">
          <Card className="border-success/30 bg-gradient-to-br from-emerald-50 to-white"><CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1"><DollarSign size={14} className="text-success" /><span className="text-[10px] text-success font-medium uppercase tracking-wider">Total Sales</span></div>
            <p className="text-xl font-bold font-outfit text-success" data-testid="kpi-total-sales">{cur()} {(data?.total_sales || 0).toLocaleString()}</p>
            <Delta value={data?.deltas?.total_sales} prev={data?.prev_values?.total_sales} label="sales" money />
          </CardContent></Card>
          <Card className="border-error/30 bg-gradient-to-br from-red-50 to-white"><CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1"><TrendingUp size={14} className="text-error" /><span className="text-[10px] text-error font-medium uppercase tracking-wider">Expenses</span></div>
            <p className="text-xl font-bold font-outfit text-error">{cur()} {(data?.total_expenses || 0).toLocaleString()}</p>
            <Delta value={data?.deltas?.total_expenses} prev={data?.prev_values?.total_expenses} label="expenses" money invert />
          </CardContent></Card>
          <Card className="border-info/30 bg-gradient-to-br from-blue-50 to-white"><CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1"><BarChart3 size={14} className="text-info" /><span className="text-[10px] text-info font-medium uppercase tracking-wider">Net</span></div>
            <p className={`text-xl font-bold font-outfit ${(data?.net || 0) >= 0 ? 'text-info' : 'text-error'}`}>{cur()} {(data?.net || 0).toLocaleString()}</p>
            <Delta value={data?.deltas?.net} prev={data?.prev_values?.net} label="net" money />
          </CardContent></Card>
          <Card className="border-purple-100 bg-gradient-to-br from-purple-50 to-white"><CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1"><ShoppingCart size={14} className="text-purple-500" /><span className="text-[10px] text-purple-600 font-medium uppercase tracking-wider">Transactions</span></div>
            <p className="text-xl font-bold font-outfit text-purple-700" data-testid="kpi-count">{data?.sales_count || 0}</p>
            <Delta value={data?.deltas?.sales_count} prev={data?.prev_values?.sales_count} label="transactions" />
          </CardContent></Card>
          <Card className="border-warning/30 bg-gradient-to-br from-amber-50 to-white"><CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1"><DollarSign size={14} className="text-warning" /><span className="text-[10px] text-warning font-medium uppercase tracking-wider">Avg Ticket</span></div>
            <p className="text-xl font-bold font-outfit text-warning">{cur()} {data?.avg_ticket || 0}</p>
            <Delta value={data?.deltas?.avg_ticket} prev={data?.prev_values?.avg_ticket} label="avg ticket" money />
          </CardContent></Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Branch Leaderboard */}
          <Card className="border-border col-span-1">
            <CardHeader className="py-3"><CardTitle className="text-sm font-outfit flex items-center gap-2"><Trophy size={14} className="text-warning" />Branch Leaderboard</CardTitle></CardHeader>
            <CardContent className="pt-0" data-testid="branch-leaderboard">
              {(data?.branch_leaderboard || []).length === 0 && <p className="text-xs text-muted-foreground text-center py-4" data-testid="empty-leaderboard">No sales found for {rangeLabel}</p>}
              {(data?.branch_leaderboard || []).map((b, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-stone-50 last:border-0">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-warning/20 text-warning' : i === 1 ? 'bg-muted text-muted-foreground' : 'bg-muted/50 text-muted-foreground'}`}>{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{b.name}</p>
                    <p className="text-[10px] text-muted-foreground">{b.count} sales</p>
                  </div>
                  <p className="font-mono text-sm font-bold text-success">{cur()} {b.total.toLocaleString()}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Hourly Sales Chart */}
          <Card className="border-border col-span-1">
            <CardHeader className="py-3"><CardTitle className="text-sm font-outfit flex items-center gap-2"><Clock size={14} className="text-info" />Hourly Breakdown</CardTitle></CardHeader>
            <CardContent>
              {(data?.hourly_chart || []).length > 0 ? (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={data.hourly_chart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" />
                    <XAxis dataKey="hour" tick={{ fontSize: 9 }} />
                    <YAxis tick={{ fontSize: 9 }} />
                    <Tooltip formatter={(v) => [`${cur()} ${v}`, 'Sales']} />
                    <Bar dataKey="amount" fill="#f97316" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-muted-foreground text-center py-12">No hourly data for {rangeLabel}</p>}
            </CardContent>
          </Card>

          {/* Payment Mode Pie + Top Cashiers */}
          <Card className="border-border col-span-1">
            <CardHeader className="py-3"><CardTitle className="text-sm font-outfit flex items-center gap-2"><Users size={14} className="text-purple-500" />Top Cashiers</CardTitle></CardHeader>
            <CardContent className="pt-0" data-testid="top-cashiers">
              {modeData.length > 0 && (
                <div className="flex justify-center mb-2">
                  <ResponsiveContainer width={120} height={100}>
                    <PieChart>
                      <Pie data={modeData} dataKey="value" cx="50%" cy="50%" innerRadius={25} outerRadius={45} paddingAngle={2}>
                        {modeData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => [`${cur()} ${v}`, '']} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-col justify-center gap-1">
                    {modeData.map((m, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-[10px]">
                        <div className="w-2 h-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }}></div>
                        <span>{m.name}: {cur()} {m.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(data?.top_cashiers || []).length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No cashier data yet</p>}
              {(data?.top_cashiers || []).map((c, i) => (
                <div key={i} className="flex items-center gap-2 py-1.5 border-b border-stone-50 last:border-0">
                  <Badge variant="outline" className="text-[10px] w-5 h-5 p-0 flex items-center justify-center">{i + 1}</Badge>
                  <span className="text-xs flex-1 truncate">{c.name}</span>
                  <span className="text-xs font-mono font-medium">{c.count}x</span>
                  <span className="text-xs font-mono font-bold text-success">{cur()} {c.total.toLocaleString()}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Live Sales Ticker */}
        <Card className="border-border">
          <CardHeader className="py-3"><CardTitle className="text-sm font-outfit flex items-center gap-2">
            <span className="w-2 h-2 bg-success rounded-full animate-pulse"></span>Live Sales Feed
          </CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="max-h-64 overflow-y-auto" data-testid="live-feed">
              {(data?.recent_sales || []).length === 0 && <p className="text-xs text-muted-foreground text-center py-8" data-testid="empty-feed">No sales found for {rangeLabel}</p>}
              {(data?.recent_sales || []).map((s, i) => (
                <div key={i} className={`flex items-center gap-3 px-4 py-2.5 border-b border-stone-50 ${i === 0 ? 'bg-success/10' : 'hover:bg-muted/50'} transition-colors`} data-testid={`sale-row-${i}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${(s.mode || '').includes('cash') ? 'bg-success/20 text-success' : (s.mode || '').includes('bank') ? 'bg-info/20 text-info' : 'bg-purple-100 text-purple-600'}`}>
                    <DollarSign size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{s.description || 'Sale'} <Badge variant="outline" className="text-[10px] ml-1">{s.mode}</Badge></p>
                    <p className="text-[10px] text-muted-foreground">{s.branch} | {s.cashier}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm font-bold text-success">{cur()} {s.amount.toLocaleString()}</p>
                    <p className="text-[10px] text-muted-foreground">{s.time ? new Date(s.time).toLocaleTimeString() : ''}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
